import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import AutoCompleteField, { AUTOCOMPLETE_LAYOUTS } from './AutoCompleteField';
import { getIsAgency } from '../../selectors/user';
import { getOrgAgencies } from '../../selectors/agencies';
import { filterAgenciesByAppPermissions, filterAgenciesByPartnerPermissions } from '../../utils/agencyPermissions';
import css from './AgencySetupDropdown.css';

const MY_ORG_VALUE = null;
// Sentinel for the locked "My Organization" tag/option in multi-select mode (agency ids are never this string).
const MY_ORG_MULTI_VALUE = '__MY_ORGANIZATION__';

const AgencySetupDropdown = ({
    value = MY_ORG_VALUE,
    onChange = () => {},
    disabled = false,
    translate = () => {},
    containerClass = '',
    selectedAppSites = null,
    selectedPartnerName = null,
    configuredAgencyIds = [],
    label = 'STATIC.PAGES.AGENCY_SETUP_DROPDOWN.LABEL',
    labelTooltip = 'STATIC.PAGES.AGENCY_SETUP_DROPDOWN.TOOLTIP',
    hideTooltip = false,
    labelClassName = '',
    layout = AUTOCOMPLETE_LAYOUTS.VERTICAL,
    containerClassName = '',
    multiSelect = false,
}) => {
    const isAgency = useSelector(getIsAgency);
    const agencies = useSelector(getOrgAgencies);
    const isLoaded = !!agencies;

    // In multi-select mode `value` is an array of agency ids.
    const selectedAgencyIds = useMemo(() => (multiSelect && Array.isArray(value) ? value : []), [multiSelect, value]);

    // Permission-aware agency options (shared by both single and multi modes).
    const agencyOptions = useMemo(() => {
        if (agencies.length === 0) {
            return [];
        }

        let filteredAgencies = agencies.map(agency => ({
            agency,
            hasAppPermission: true,
            hasPartnerPermission: true,
        }));

        // Apply app site filtering if selectedAppSites is provided
        if (selectedAppSites?.length) {
            const appFiltered = filterAgenciesByAppPermissions(agencies, selectedAppSites);
            filteredAgencies = filteredAgencies.map(({ agency }) => {
                const appResult = appFiltered.find(({ agency: a }) => a.agency_id === agency.agency_id);
                return {
                    agency,
                    hasAppPermission: appResult?.hasPermission ?? true,
                    hasPartnerPermission: true,
                };
            });
        }

        // Apply partner filtering if selectedPartnerName is provided
        if (selectedPartnerName) {
            const partnerFiltered = filterAgenciesByPartnerPermissions(
                filteredAgencies.map(({ agency }) => agency),
                selectedPartnerName
            );
            filteredAgencies = filteredAgencies.map(({ agency, hasAppPermission }) => {
                const partnerResult = partnerFiltered.find(({ agency: a }) => a.agency_id === agency.agency_id);
                return {
                    agency,
                    hasAppPermission,
                    hasPartnerPermission: partnerResult?.hasPermission ?? true,
                };
            });
        }

        return filteredAgencies.map(({ agency, hasAppPermission, hasPartnerPermission }) => {
            const hasPermission = hasAppPermission && hasPartnerPermission;
            const isAlreadyConfigured = configuredAgencyIds.includes(agency.agency_id);

            let extra = null;
            if (isAlreadyConfigured) {
                extra = translate('STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.CONFIGURATION_ALREADY_EXISTS');
            } else if (!hasPermission) {
                // With source permission the block is the app; otherwise it's the source (partner) permission.
                extra = translate(
                    hasPartnerPermission
                        ? 'STATIC.PAGES.AGENCY_SETUP_DROPDOWN.APP_NOT_PERMITTED'
                        : 'STATIC.PAGES.AGENCY_SETUP_DROPDOWN.SOURCE_NOT_PERMITTED'
                );
            }

            return {
                value: agency.agency_id,
                label: agency.display_name,
                // Use a URL (iconSrc), not a React element, so options stay JSON-serializable for TagInput (multi mode).
                ...(agency.company_logo && { iconSrc: agency.company_logo }),
                isDisabled: !hasPermission || isAlreadyConfigured,
                extra,
            };
        });
    }, [agencies, translate, selectedAppSites, selectedPartnerName, configuredAgencyIds]);

    // Single-select menu options ("My Organization" + agencies). In disabled state with an
    // unknown value, show the raw value so it renders even without a matching agency.
    const options = useMemo(() => {
        if (disabled && value !== MY_ORG_VALUE && !agencies.find(agency => agency.agency_id === value)) {
            return [{ value, label: value }];
        }

        const isMyOrgConfigured = configuredAgencyIds.includes(MY_ORG_VALUE);
        return [
            {
                value: MY_ORG_VALUE,
                label: translate('STATIC.PAGES.AGENCY_SETUP_DROPDOWN.MY_ORG'),
                isDisabled: isMyOrgConfigured,
                extra: isMyOrgConfigured
                    ? translate('STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.CONFIGURATION_ALREADY_EXISTS')
                    : null,
            },
            ...agencyOptions,
        ];
    }, [agencyOptions, translate, disabled, value, agencies, configuredAgencyIds]);

    const selectedOption = useMemo(() => {
        return options.find(opt => opt.value === value) || options[0];
    }, [options, value]);

    // Multi-select: a locked "My Organization" pinned option (with a divider) at the top of the menu,
    // followed by the not-yet-selected agencies (already-selected ones are filtered out, leaving only
    // the locked My-Org row in the menu). "My Organization" is UI-only and never part of the stored value.
    const multiMenuOptions = useMemo(() => {
        const selectableAgencies = agencyOptions.filter(opt => !selectedAgencyIds.includes(opt.value));

        return [
            {
                value: MY_ORG_MULTI_VALUE,
                label: translate('STATIC.PAGES.AGENCY_SETUP_DROPDOWN.MY_ORG'),
                isDisabled: true,
                className: css.myOrgOption,
            },
            ...selectableAgencies,
        ];
    }, [agencyOptions, translate, selectedAgencyIds]);

    // Multi-select selected tags: the locked "My Organization" tag plus the selected agencies.
    const multiTags = useMemo(() => {
        const myOrgTag = {
            value: MY_ORG_MULTI_VALUE,
            label: translate('STATIC.PAGES.AGENCY_SETUP_DROPDOWN.MY_ORG'),
            deleteEnabled: false,
        };
        const agencyTags = selectedAgencyIds.map(
            id => agencyOptions.find(opt => opt.value === id) || { value: id, label: id }
        );
        return [myOrgTag, ...agencyTags];
    }, [selectedAgencyIds, agencyOptions, translate]);

    const onMultiChange = currentTags => {
        // Emit the selected agency ids as an array (the locked "My Organization" tag is UI-only).
        const ids = (currentTags || []).map(tag => tag.value).filter(tagValue => tagValue !== MY_ORG_MULTI_VALUE);
        onChange(ids);
    };

    // Don't show for agencies, if not loaded, or if no related agencies
    if (isAgency || !isLoaded || agencies.length === 0) {
        return null;
    }

    return (
        <div className={`${css.container} ${containerClassName}`}>
            <AutoCompleteField
                label={label}
                labelTooltip={hideTooltip ? null : labelTooltip}
                labelClassName={labelClassName}
                layout={layout}
                options={multiSelect ? multiMenuOptions : options}
                value={multiSelect ? multiTags : selectedOption}
                isMulti={multiSelect}
                disabled={disabled}
                onChange={multiSelect ? onMultiChange : option => onChange(option?.value ?? MY_ORG_VALUE)}
                containerClass={containerClass}
                placeholder="STATIC.PAGES.AGENCY_SETUP_DROPDOWN.PLACEHOLDER"
            />
        </div>
    );
};

AgencySetupDropdown.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    translate: PropTypes.func,
    containerClass: PropTypes.string,
    selectedAppSites: PropTypes.arrayOf(
        PropTypes.shape({
            site_public_id: PropTypes.string,
            publicId: PropTypes.string,
        })
    ),
    selectedPartnerName: PropTypes.string,
    configuredAgencyIds: PropTypes.arrayOf(PropTypes.string),
    label: PropTypes.string,
    labelTooltip: PropTypes.string,
    hideTooltip: PropTypes.bool,
    labelClassName: PropTypes.string,
    layout: PropTypes.oneOf(Object.values(AUTOCOMPLETE_LAYOUTS)),
    containerClassName: PropTypes.string,
    multiSelect: PropTypes.bool,
};

export default withLocalize(AgencySetupDropdown);
